import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Star, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";

const Stars = ({ n }) => (
  <span className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= Math.round(n) ? "fill-[#C47A37] text-[#C47A37]" : "text-[#D9CDBF]"} />)}</span>
);

function ProductAverages({ rows }) {
  return (
    <div className="bg-white border border-[#E6DFD5]" data-testid="reviews-product-averages">
      <p className="p-4 text-sm border-b border-[#E6DFD5]">Average rating per product (shown ratings only — same as customers see)</p>
      {rows.map((p) => (
        <div key={p.product_id} className="px-4 py-2.5 flex items-center gap-4 text-sm border-b border-[#E6DFD5] last:border-0" data-testid={`avg-row-${p.product_id}`}>
          <span className="flex-1">{p.name}</span><Stars n={p.avg} /><span className="font-mono w-12 text-right">{p.avg.toFixed(2)}</span>
          <span className="text-xs text-[#6E6259] w-32 text-right">{p.count} ratings · {p.visible} shown</span>
        </div>
      ))}
      {!rows.length && <p className="p-4 text-sm text-[#6E6259]">No ratings yet.</p>}
    </div>
  );
}

function ManualDialog({ open, setOpen, onSaved }) {
  const [products, setProducts] = useState([]);
  const blank = { product_id: "", rating: 0, comment: "", user_name: "", anonymous: false };
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) { api.get("/admin/products").then((r) => setProducts(r.data)); setF(blank); } }, [open]); // eslint-disable-line
  const save = async () => {
    try { await api.post("/admin/reviews", f); toast.success("Manual rating added"); setOpen(false); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-none max-w-lg space-y-3" data-testid="manual-review-dialog">
        <DialogTitle className="font-serif text-2xl font-normal">Add manual rating</DialogTitle>
        <Field label="Product"><select value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} className={inputCls} data-testid="manual-review-product"><option value="">Choose product...</option>{products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} ({p.type})</option>)}</select></Field>
        <Field label="Stars"><div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => <button key={i} type="button" onClick={() => setF({ ...f, rating: i })} data-testid={`manual-review-star-${i}`}><Star size={24} className={i <= f.rating ? "fill-[#C47A37] text-[#C47A37]" : "text-[#C9BBAA]"} /></button>)}</div></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.anonymous} onChange={(e) => setF({ ...f, anonymous: e.target.checked })} data-testid="manual-review-anonymous" />Anonymous (shown as "Anonim")</label>
        {!f.anonymous && <Field label="Customer name shown"><input value={f.user_name} onChange={(e) => setF({ ...f, user_name: e.target.value })} className={inputCls} data-testid="manual-review-name" /></Field>}
        <Field label="Comment (optional)"><textarea rows={3} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} className={inputCls} data-testid="manual-review-comment" /></Field>
        <Btn onClick={save} disabled={!f.product_id || !f.rating} data-testid="manual-review-save">Save rating</Btn>
      </DialogContent>
    </Dialog>
  );
}

function ReviewList({ title, rows, toggle, del, testid, canEdit }) {
  return (
    <section className="space-y-2" data-testid={testid}>
      <h2 className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{title.toUpperCase()} ({rows.length})</h2>
      <div className="bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5] max-h-[820px] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.review_id} className={`p-4 flex flex-wrap items-start gap-4 text-sm ${r.visible === false ? "opacity-50" : ""}`} data-testid={`review-row-${r.review_id}`}>
            <div className="flex-1 min-w-[220px] space-y-1">
              <div className="flex flex-wrap items-center gap-3"><Stars n={r.rating} /><span className="font-medium">{r.user_name}</span>
                {r.anonymous && <span className="text-[10px] font-mono border border-[#E6DFD5] px-1.5">ANONIM</span>}
                <span className="text-xs text-[#6E6259]">{new Date(r.created_at).toLocaleString()}</span></div>
              <p className="text-xs text-[#8C4F27]">{r.product_name}</p>
              {r.comment && <p>{r.comment}</p>}
            </div>
            {canEdit && <label className="flex items-center gap-2 text-xs"><Switch checked={r.visible !== false} onCheckedChange={() => toggle(r)} data-testid={`review-visible-${r.review_id}`} />{r.visible === false ? "Hidden" : "Shown"}</label>}
            {canEdit && <button onClick={() => del(r)} aria-label="Delete" className="text-[#9E2A2B]" data-testid={`review-delete-${r.review_id}`}><Trash2 size={14} /></button>}
          </div>
        ))}
        {!rows.length && <p className="p-6 text-sm text-[#6E6259]">No ratings found.</p>}
      </div>
    </section>
  );
}

export default function Reviews() {
  const { user } = useApp();
  const canEdit = user.role === "superadmin";
  const [manual, setManual] = useState(false);
  const [d, setD] = useState({ reviews: [], products: [] });
  const [f, setF] = useState({ q: "", rating: 0, sort: "newest" });
  const load = () => api.get("/admin/reviews", { params: f }).then((r) => setD(r.data));
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [f]); // eslint-disable-line
  const toggle = async (r) => { try { await api.patch(`/admin/reviews/${r.review_id}`, { visible: r.visible === false }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (r) => {
    if (!window.confirm("Delete this rating and comment permanently?")) return;
    try { await api.delete(`/admin/reviews/${r.review_id}`); toast.success("Rating deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <AdminTitle action={canEdit && <Btn onClick={() => setManual(true)} className="flex items-center gap-2" data-testid="add-manual-review-btn"><Plus size={14} />Add manual rating</Btn>}>Ratings & Reviews</AdminTitle>
      <ManualDialog open={manual} setOpen={setManual} onSaved={load} />
      <ProductAverages rows={d.products} />
      <div className="flex flex-wrap gap-2">
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search product, customer or comment..." className={`${inputCls} sm:w-72`} data-testid="reviews-search" />
        <select value={f.rating} onChange={(e) => setF({ ...f, rating: Number(e.target.value) })} className={`${inputCls} !w-36`} data-testid="reviews-rating-filter">
          <option value={0}>All stars</option>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
        </select>
        <select value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} className={`${inputCls} !w-48`} data-testid="reviews-sort">
          <option value="newest">Newest</option><option value="rating_desc">Highest rating first</option><option value="rating_asc">Lowest rating first</option>
        </select>
      </div>
      <ReviewList title="Automatic ratings (from buyers)" testid="reviews-auto" rows={d.reviews.filter((r) => r.source !== "manual")} toggle={toggle} del={del} canEdit={canEdit} />
      <ReviewList title="Manual ratings (by superadmin)" testid="reviews-manual" rows={d.reviews.filter((r) => r.source === "manual")} toggle={toggle} del={del} canEdit={canEdit} />
    </div>
  );
}
